import React, { useState, useEffect } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { supabase } from '@/integrations/supabase/client';
import { Home, Search, Eye, Trash2, MapPin, Calendar, DollarSign, CheckCircle, XCircle, Shield } from 'lucide-react';
import { toast } from 'sonner';

interface Listing {
  id: string;
  title: string;
  address: string;
  price_egp: number;
  deposit_egp: number;
  unit_type: string;
  furnished: boolean;
  verified: boolean;
  status: string;
  available_from: string;
  created_at: string;
  owner_id: string;
  university_id: string;
  distance_km: number;
  description: string;
  amenities: string[];
  lease_min_months: number;
  gender_rules: string;
  approval_status: string;
  profiles: {
    full_name: string;
    email: string;
  } | null;
  universities: {
    name_en: string;
    name_ar: string;
    city: string;
  } | null;
  photos: string[];
  property_universities: Array<{
    universities: {
      name_en: string;
      name_ar: string;
    };
  }>;
}

interface University {
  id: string;
  name_en: string;
  name_ar: string;
  city: string;
}

const ListingsManagement = () => {
  const { t, language } = useLanguage();
  const [listings, setListings] = useState<Listing[]>([]);
  const [universities, setUniversities] = useState<University[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [verificationFilter, setVerificationFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [universityFilter, setUniversityFilter] = useState('all');
  const [selectedListing, setSelectedListing] = useState<Listing | null>(null);

  useEffect(() => {
    fetchListings();
    fetchUniversities();
  }, []);

  const fetchListings = async () => {
    try {
      setLoading(true);
      
      const { data: listingsData, error: listingsError } = await supabase
        .from('listings')
        .select(`
          *,
          universities (
            name_en,
            name_ar,
            city
          ),
          property_universities (
            universities (
              name_en,
              name_ar
            )
          )
        `)
        .order('created_at', { ascending: false });

      if (listingsError) {
        console.error('Error fetching listings:', listingsError);
        toast.error('Failed to load listings');
        return;
      }

      const ownerIds = [...new Set(listingsData?.map(listing => listing.owner_id) || [])];
      
      const { data: profilesData, error: profilesError } = await supabase
        .from('profiles')
        .select('id, full_name, email')
        .in('id', ownerIds);

      if (profilesError) {
        console.error('Error fetching profiles:', profilesError);
      }

      const profilesMap = new Map();
      profilesData?.forEach(profile => {
        profilesMap.set(profile.id, profile);
      });

      const transformedListings = (listingsData || []).map(listing => ({
        ...listing,
        profiles: profilesMap.get(listing.owner_id) || null,
        universities: listing.universities as { name_en: string; name_ar: string; city: string } | null,
        property_universities: listing.property_universities || []
      }));

      setListings(transformedListings);
    } catch (error) {
      console.error('Exception fetching listings:', error);
      toast.error('Failed to load listings');
    } finally {
      setLoading(false);
    }
  };

  const fetchUniversities = async () => {
    try {
      const { data, error } = await supabase
        .from('universities')
        .select('id, name_en, name_ar, city')
        .order('name_en');

      if (error) {
        console.error('Error fetching universities:', error);
        return;
      }

      setUniversities(data || []);
    } catch (error) {
      console.error('Exception fetching universities:', error);
    }
  };

  const deleteListing = async (listingId: string, listingTitle: string) => {
    try {
      console.log('Attempting to delete listing:', listingId);
      
      // First delete related property_universities records
      const { error: propertyUniversitiesError } = await supabase
        .from('property_universities')
        .delete()
        .eq('listing_id', listingId);

      if (propertyUniversitiesError) {
        console.error('Error deleting property universities:', propertyUniversitiesError);
        toast.error('Failed to delete listing dependencies');
        return;
      }

      // Then delete the listing
      const { error: listingError } = await supabase
        .from('listings')
        .delete()
        .eq('id', listingId);

      if (listingError) {
        console.error('Error deleting listing:', listingError);
        toast.error('Failed to delete listing: ' + listingError.message);
        return;
      }

      toast.success(`Listing "${listingTitle}" deleted successfully`);
      
      // Refresh the listings
      await fetchListings();
    } catch (error) {
      console.error('Exception deleting listing:', error);
      toast.error('An unexpected error occurred while deleting the listing');
    }
  };

  const filteredListings = listings.filter(listing => {
    const matchesSearch = listing.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         listing.address.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         listing.profiles?.full_name?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || listing.status === statusFilter;
    const matchesVerification = verificationFilter === 'all' || 
                               (verificationFilter === 'verified' && listing.verified) ||
                               (verificationFilter === 'unverified' && !listing.verified);
    const matchesType = typeFilter === 'all' || listing.unit_type === typeFilter;
    const matchesUniversity = universityFilter === 'all' || listing.university_id === universityFilter;

    return matchesSearch && matchesStatus && matchesVerification && matchesType && matchesUniversity;
  });

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-green-100 text-green-800">{t('active', 'نشط', 'Active')}</Badge>;
      case 'inactive':
        return <Badge variant="secondary">{t('inactive', 'غير نشط', 'Inactive')}</Badge>;
      case 'pending':
        return <Badge className="bg-yellow-100 text-yellow-800">{t('pending', 'معلق', 'Pending')}</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getUnitTypeLabel = (type: string) => {
    const labels = {
      room: { en: 'Private Room', ar: 'غرفة خاصة' },
      shared: { en: 'Shared Room', ar: 'غرفة مشتركة' },
      entire: { en: 'Entire Unit', ar: 'وحدة كاملة' }
    };
    return labels[type as keyof typeof labels]?.[language === 'ar' ? 'ar' : 'en'] || type;
  };

  const getGenderLabel = (gender: string) => {
    const labels = {
      any: { en: 'Any', ar: 'أي جنس' },
      female: { en: 'Female Only', ar: 'إناث فقط' },
      male: { en: 'Male Only', ar: 'ذكور فقط' },
      mixed: { en: 'Mixed', ar: 'مختلط' }
    };
    return labels[gender as keyof typeof labels]?.[language === 'ar' ? 'ar' : 'en'] || gender;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Home className="h-5 w-5" />
          {t('listings_management', 'إدارة الإعلانات', 'Listings Management')}
        </CardTitle>
        <CardDescription>
          {t('manage_all_property_listings', 'إدارة جميع إعلانات العقارات', 'Manage all property listings')}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={t('search_listings', 'البحث في الإعلانات', 'Search listings...')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[140px]">
              <SelectValue placeholder={t('status', 'الحالة', 'Status')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('all_statuses', 'جميع الحالات', 'All Statuses')}</SelectItem>
              <SelectItem value="active">{t('active', 'نشط', 'Active')}</SelectItem>
              <SelectItem value="inactive">{t('inactive', 'غير نشط', 'Inactive')}</SelectItem>
              <SelectItem value="pending">{t('pending', 'معلق', 'Pending')}</SelectItem>
            </SelectContent>
          </Select>

          <Select value={verificationFilter} onValueChange={setVerificationFilter}>
            <SelectTrigger className="w-[140px]">
              <SelectValue placeholder={t('verification', 'التحقق', 'Verification')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('all', 'الكل', 'All')}</SelectItem>
              <SelectItem value="verified">{t('verified', 'مُتحقق', 'Verified')}</SelectItem>
              <SelectItem value="unverified">{t('unverified', 'غير مُتحقق', 'Unverified')}</SelectItem>
            </SelectContent>
          </Select>

          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-[140px]">
              <SelectValue placeholder={t('type', 'النوع', 'Type')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('all_types', 'جميع الأنواع', 'All Types')}</SelectItem>
              <SelectItem value="room">{t('private_room', 'غرفة خاصة', 'Private Room')}</SelectItem>
              <SelectItem value="shared">{t('shared_room', 'غرفة مشتركة', 'Shared Room')}</SelectItem>
              <SelectItem value="entire">{t('entire_unit', 'وحدة كاملة', 'Entire Unit')}</SelectItem>
            </SelectContent>
          </Select>

          <Select value={universityFilter} onValueChange={setUniversityFilter}>
            <SelectTrigger className="w-[160px]">
              <SelectValue placeholder={t('university', 'الجامعة', 'University')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('all_universities', 'جميع الجامعات', 'All Universities')}</SelectItem>
              {universities.map((university) => (
                <SelectItem key={university.id} value={university.id}>
                  {language === 'ar' ? university.name_ar : university.name_en}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Results Summary */}
        <div className="mb-4 text-sm text-muted-foreground">
          {t('showing_results', 'عرض النتائج', 'Showing')} {filteredListings.length} {t('of', 'من', 'of')} {listings.length} {t('listings', 'إعلان', 'listings')}
        </div>

        {/* Listings Table */}
        <div className="rounded-md border overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[300px]">{t('property', 'العقار', 'Property')}</TableHead>
                <TableHead className="w-[200px]">{t('owner', 'المالك', 'Owner')}</TableHead>
                <TableHead className="w-[120px]">{t('price', 'السعر', 'Price')}</TableHead>
                <TableHead className="w-[100px]">{t('status', 'الحالة', 'Status')}</TableHead>
                <TableHead className="w-[200px]">{t('actions', 'الإجراءات', 'Actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredListings.map((listing) => (
                <TableRow key={listing.id}>
                  <TableCell className="w-[300px]">
                    <div className="flex items-center gap-3 min-w-0">
                      {listing.photos?.[0] && (
                        <img 
                          src={listing.photos[0]} 
                          alt={listing.title}
                          className="w-12 h-12 rounded-lg object-cover flex-shrink-0"
                        />
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="font-medium truncate" title={listing.title}>
                          {listing.title}
                        </div>
                        <div className="text-sm text-muted-foreground flex items-center gap-1">
                          <MapPin className="h-3 w-3 flex-shrink-0" />
                          <span className="truncate">{listing.universities?.city}</span>
                        </div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="w-[200px]">
                    <div className="font-medium truncate" title={listing.profiles?.full_name || 'N/A'}>
                      {listing.profiles?.full_name || 'N/A'}
                    </div>
                  </TableCell>
                  <TableCell className="w-[120px]">
                    <div className="flex items-center gap-1 min-w-0">
                      <DollarSign className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                      <span className="font-medium text-sm truncate">
                        {listing.price_egp.toLocaleString()} EGP
                      </span>
                    </div>
                  </TableCell>
                  <TableCell className="w-[100px]">
                    {getStatusBadge(listing.status)}
                  </TableCell>
                  <TableCell className="w-[200px]">
                    <div className="flex items-center gap-2">
                      <Dialog>
                        <DialogTrigger asChild>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setSelectedListing(listing)}
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            {t('details', 'التفاصيل', 'Details')}
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
                          <DialogHeader>
                            <DialogTitle>{selectedListing?.title}</DialogTitle>
                            <DialogDescription>
                              {t('listing_details', 'تفاصيل الإعلان', 'Listing Details')}
                            </DialogDescription>
                          </DialogHeader>
                          {selectedListing && (
                            <div className="space-y-6">
                              {/* Images */}
                              {selectedListing.photos && selectedListing.photos.length > 0 && (
                                <div className="grid grid-cols-2 gap-4">
                                  {selectedListing.photos.slice(0, 4).map((photo, index) => (
                                    <div key={index} className="aspect-video bg-muted rounded-lg overflow-hidden">
                                      <img 
                                        src={photo} 
                                        alt={`${selectedListing.title} - Image ${index + 1}`}
                                        className="w-full h-full object-cover"
                                      />
                                    </div>
                                  ))}
                                </div>
                              )}

                              {/* Basic Info */}
                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <h4 className="font-semibold mb-2">{t('basic_info', 'المعلومات الأساسية', 'Basic Information')}</h4>
                                  <div className="space-y-2 text-sm">
                                    <div><strong>{t('address', 'العنوان', 'Address')}:</strong> {selectedListing.address}</div>
                                    <div><strong>{t('price', 'السعر', 'Price')}:</strong> {selectedListing.price_egp.toLocaleString()} EGP/month</div>
                                    <div><strong>{t('deposit', 'التأمين', 'Deposit')}:</strong> {selectedListing.deposit_egp.toLocaleString()} EGP</div>
                                    <div><strong>{t('unit_type', 'نوع الوحدة', 'Unit Type')}:</strong> {getUnitTypeLabel(selectedListing.unit_type)}</div>
                                    <div><strong>{t('gender_rules', 'قواعد الجنس', 'Gender Rules')}:</strong> {getGenderLabel(selectedListing.gender_rules)}</div>
                                    <div><strong>{t('furnished', 'مفروش', 'Furnished')}:</strong> {selectedListing.furnished ? t('yes', 'نعم', 'Yes') : t('no', 'لا', 'No')}</div>
                                    <div><strong>{t('min_lease', 'أقل مدة إيجار', 'Min Lease')}:</strong> {selectedListing.lease_min_months} months</div>
                                  </div>
                                </div>
                                <div>
                                  <h4 className="font-semibold mb-2">{t('owner_info', 'معلومات المالك', 'Owner Information')}</h4>
                                  <div className="space-y-2 text-sm">
                                    <div><strong>{t('name', 'الاسم', 'Name')}:</strong> {selectedListing.profiles?.full_name || 'N/A'}</div>
                                    <div><strong>{t('email', 'البريد الإلكتروني', 'Email')}:</strong> {selectedListing.profiles?.email || 'N/A'}</div>
                                    <div><strong>{t('university', 'الجامعة', 'University')}:</strong> {language === 'ar' ? selectedListing.universities?.name_ar : selectedListing.universities?.name_en}</div>
                                    <div><strong>{t('distance', 'المسافة', 'Distance')}:</strong> {selectedListing.distance_km}km from campus</div>
                                  </div>
                                </div>
                              </div>

                              {/* Description */}
                              <div>
                                <h4 className="font-semibold mb-2">{t('description', 'الوصف', 'Description')}</h4>
                                <p className="text-sm text-muted-foreground">{selectedListing.description}</p>
                              </div>

                              {/* Amenities */}
                              {selectedListing.amenities && selectedListing.amenities.length > 0 && (
                                <div>
                                  <h4 className="font-semibold mb-2">{t('amenities', 'المرافق', 'Amenities')}</h4>
                                  <div className="flex flex-wrap gap-2">
                                    {selectedListing.amenities.map((amenity, index) => (
                                      <Badge key={index} variant="outline" className="text-xs">
                                        {amenity}
                                      </Badge>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {/* Dates */}
                              <div className="grid grid-cols-2 gap-4 text-sm">
                                <div>
                                  <strong>{t('available_from', 'متاح من', 'Available From')}:</strong> {new Date(selectedListing.available_from).toLocaleDateString()}
                                </div>
                                <div>
                                  <strong>{t('created_on', 'أنشئ في', 'Created On')}:</strong> {new Date(selectedListing.created_at).toLocaleDateString()}
                                </div>
                              </div>
                            </div>
                          )}
                        </DialogContent>
                      </Dialog>
                      
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            variant="destructive"
                            size="sm"
                          >
                            <Trash2 className="h-4 w-4 mr-1" />
                            {t('delete', 'حذف', 'Delete')}
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>
                              {t('confirm_delete', 'تأكيد الحذف', 'Confirm Delete')}
                            </AlertDialogTitle>
                            <AlertDialogDescription>
                              {t('delete_listing_warning', 
                                `هل أنت متأكد من حذف "${listing.title}"؟ هذا الإجراء لا يمكن التراجع عنه.`,
                                `Are you sure you want to delete "${listing.title}"? This action cannot be undone.`
                              )}
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>
                              {t('cancel', 'إلغاء', 'Cancel')}
                            </AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => deleteListing(listing.id, listing.title)}
                              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            >
                              {t('delete', 'حذف', 'Delete')}
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {filteredListings.length === 0 && (
          <div className="text-center py-8 text-muted-foreground">
            <Home className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>{t('no_listings_found', 'لم يتم العثور على إعلانات', 'No listings found')}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ListingsManagement;
